import { useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Github, Linkedin, Mail, BookOpen, ArrowUp } from 'lucide-react'
import {
  prefersReducedMotion,
  initGSAP,
  scrollToSection,
  scrollToTop,
} from '../lib/motion'
import { PROFILE } from '../data/profile'

const CURRENT_YEAR = new Date().getFullYear()

const SOCIALS = [
  {
    label: 'GitHub',
    href: PROFILE.github,
    icon: Github,
  },
  {
    label: 'LinkedIn',
    href: PROFILE.linkedin,
    icon: Linkedin,
  },
  {
    label: 'Medium',
    href: PROFILE.medium,
    icon: BookOpen,
  },
  {
    label: 'Email',
    href: `mailto:${PROFILE.email}`,
    icon: Mail,
  },
]

const LINKS = [
  { label: 'About', id: 'about' },
  { label: 'Experience', id: 'experience' },
  { label: 'Projects', id: 'projects' },
  { label: 'Contact', id: 'contact' },
]

const Footer: React.FC = () => {
  const footRef = useRef<HTMLElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const onHome = location.pathname === '/'

  const handleNav = (id: string) => {
    if (onHome) {
      scrollToSection(`#${id}`)
    } else {
      navigate('/', { state: { scrollTo: id } })
    }
  }

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        '[data-footer-reveal]',
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.08,
          ease: 'power3.out',
          scrollTrigger: { trigger: footRef.current, start: 'top 92%', once: true },
        }
      )
    }, footRef)
    return () => ctx.revert()
  }, [])

  return (
    <footer
      ref={footRef}
      className="relative bg-neutral-950 text-neutral-300 border-t border-neutral-800"
    >
      <div className="container-width py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr]">
          <div data-footer-reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-neutral-900 border border-white/8 text-primary-400 font-mono text-base font-semibold">
                E.
              </span>
              <div>
                <div className="text-white font-semibold leading-none">
                  Ezzddinne Mhamdi
                </div>
                <div className="mono-label mt-2 !text-neutral-500">
                  Junior Platform Engineer
                </div>
              </div>
            </div>
            <p className="text-sm text-neutral-400 leading-relaxed max-w-sm mb-8">
              Cloud infrastructure, Kubernetes platforms, GitOps workflows, and
              developer self-service systems — built, automated, secured and
              observed.
            </p>
            <div className="flex gap-3">
              {SOCIALS.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border border-white/8 text-neutral-400 hover:text-primary-300 hover:border-primary-400/40 transition-colors duration-200"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          <div data-footer-reveal>
            <h3 className="mono-label mb-5 !text-primary-500">Sitemap</h3>
            <ul className="space-y-3">
              {LINKS.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => handleNav(link.id)}
                    className="text-sm text-neutral-400 hover:text-primary-300 transition-colors duration-200 group inline-flex items-center gap-2"
                  >
                    <span className="w-0 h-px bg-primary-400/60 group-hover:w-3 transition-all duration-200" />
                    {link.label}
                  </button>
                  {link.id === 'projects' && (
                    <ul className="ml-5 mt-2 space-y-2">
                      {[
                        { to: '/projects/catalyst', label: 'Project Catalyst' },
                        { to: '/projects/azure-zero-trust', label: 'Azure Zero Trust AKS' },
                      ].map((child) => (
                        <li key={child.to}>
                          <Link
                            to={child.to}
                            className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-primary-300 transition-colors duration-200 group/child"
                          >
                            <span className="w-0 h-px bg-primary-400/40 group-hover/child:w-2 transition-all duration-200" />
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div data-footer-reveal>
            <h3 className="mono-label mb-5 !text-primary-500">Contact</h3>
            <ul className="space-y-3 text-sm text-neutral-400">
              <li>
                <a
                  href={`mailto:${PROFILE.email}`}
                  className="hover:text-primary-300 transition-colors duration-200"
                >
                  {PROFILE.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${PROFILE.phoneHref}`}
                  className="hover:text-primary-300 transition-colors duration-200"
                >
                  {PROFILE.phone}
                </a>
              </li>
              <li>{PROFILE.location}</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-neutral-900 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-neutral-500">
            © {CURRENT_YEAR} Ezzddinne Mhamdi. All rights reserved.
          </p>
          <button
            onClick={() => scrollToTop(true)}
            className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-primary-300 transition-colors group"
          >
            <span className="w-0 h-px bg-primary-400/60 group-hover:w-2.5 transition-all duration-200" />
            Back to top
            <ArrowUp size={12} />
          </button>
        </div>
      </div>
    </footer>
  )
}

export default Footer